import { AgSeriesTooltipRendererParams } from "ag-charts-community";
import { CapitalActivityChartValue } from "gen/model/capitalActivityChartValue";
import { CapitalHistoryChartValue } from "gen/model/capitalHistoryChartValue";
import { ChartStatStatAnyOf } from "gen/model/chartStatStatAnyOf";
import { CreditFacilityChartValue } from "gen/model/creditFacilityChartValue";
import { FeesChartValue } from "gen/model/feesChartValue";
import { GenericChartDataDataItem } from "gen/model/genericChartDataDataItem";
import { IRRChartValue } from "gen/model/iRRChartValue";
import { MoneyChartStatValue } from "gen/model/moneyChartStatValue";
import { NavChartValue } from "gen/model/navChartValue";
import { PercentageChartStatValue } from "gen/model/percentageChartStatValue";
import { decimalOrZero } from "shared/utils/DecimalUtils";
import { formatPercentage } from "shared/utils/formattingUtils";
import { divideMoneyStrings, formatMoney } from "shared/utils/MoneyUtils";
import colors from "theme/colors";

type ChartStatValueFormatter<T extends string = ChartStatStatAnyOf["type"]> = (
  value: Extract<
    ChartStatStatAnyOf,
    {
      type: T;
    }
  >,
) => string;

type ChartStatValueFormatters<T extends string = ChartStatStatAnyOf["type"]> = {
  [K in T]: ChartStatValueFormatter<K>;
};

export const chartStatFormatters: ChartStatValueFormatters = {
  percentage: (value: PercentageChartStatValue) =>
    value.value ? formatPercentage(value.value) : "",
  money: (value: MoneyChartStatValue) =>
    value.value ? formatMoney(value.value) : "",
};

export const tooltipRenderer = (
  key?: string | null,
): ((params: AgSeriesTooltipRendererParams) => {
  title: string;
  content: string;
  color?: string;
}) => {
  switch (key) {
    case "gross_irr":
      return (params: AgSeriesTooltipRendererParams<IRRChartValue>) => ({
        title: params.title ?? "",
        content: `${params.datum.quarter}: ${formatPercentage(params.datum.gross_irr)}`,
      });
    case "net_irr":
      return (params: AgSeriesTooltipRendererParams<IRRChartValue>) => ({
        title: params.title ?? "",
        content: `${params.datum.quarter}: ${formatPercentage(params.datum.net_irr)}`,
      });
    case "nav":
      return (params: AgSeriesTooltipRendererParams<NavChartValue>) => ({
        title: params.title ?? "",
        content: `${params.datum.quarter}: ${formatMoney(params.datum.nav.toString())}`,
      });
    case "called":
      return (
        params: AgSeriesTooltipRendererParams<CapitalHistoryChartValue>,
      ) => ({
        title: params.title ?? "",
        content: `${params.datum.year}: ${formatMoney(params.datum.called.toString())}`,
        color: colors.cyan[600],
      });
    case "distributed":
      return (
        params: AgSeriesTooltipRendererParams<CapitalHistoryChartValue>,
      ) => ({
        title: params.title ?? "",
        content: `${params.datum.year}: ${formatMoney(params.datum.distributed.toString())}`,
      });
    case "capital_activity":
      return (
        params: AgSeriesTooltipRendererParams<CapitalActivityChartValue>,
      ) => ({
        title: params.title ?? "",
        content: `${params.datum.category}: ${formatMoney(params.datum.value.toString())}`,
      });
    case "net_fees":
      return (params: AgSeriesTooltipRendererParams<FeesChartValue>) => ({
        title: params.title ?? "",
        content: `${params.datum.quarter}: ${formatMoney(params.datum.net_fees.toString())}`,
      });
    case "fee_breaks":
      return (params: AgSeriesTooltipRendererParams<FeesChartValue>) => ({
        title: params.title ?? "",
        content: `${params.datum.quarter}: ${formatMoney(params.datum.fee_breaks.toString())}`,
      });
    case "gross_fees":
      return (params: AgSeriesTooltipRendererParams<FeesChartValue>) => ({
        title: params.title ?? "",
        content: `${params.datum.quarter}: ${formatMoney(params.datum.gross_fees.toString())}`,
      });
    case "offsets":
      return (params: AgSeriesTooltipRendererParams<FeesChartValue>) => ({
        title: params.title ?? "",
        content: params.datum.offsets
          ? `${params.datum.quarter}: ${formatMoney(params.datum.offsets.toString())}`
          : "",
        color: colors.cyan[600],
      });
    case "amount_outstanding":
      return (
        params: AgSeriesTooltipRendererParams<CreditFacilityChartValue>,
      ) => ({
        title: params.title ?? "",
        content: `Principal Outstanding: ${formatMoney(params.datum.amount_outstanding)}`,
      });
    case "amount_available":
      return (
        params: AgSeriesTooltipRendererParams<CreditFacilityChartValue>,
      ) => ({
        title: params.title ?? "",
        content: `Amount Available: ${formatMoney(params.datum.amount_available)}`,
      });
    default:
      return (params: AgSeriesTooltipRendererParams) => ({
        title: params.title ?? "",
        content: "",
      });
  }
};

export const chartDataConverter = (data: GenericChartDataDataItem) => {
  switch (data.type) {
    case "IRR":
      return {
        quarter: data.quarter,
        gross_irr: decimalOrZero(data.gross_irr).toNumber(),
        net_irr: decimalOrZero(data.net_irr).toNumber(),
      };
    case "Nav":
      return {
        quarter: data.quarter,
        nav: decimalOrZero(data.nav).round().toNumber(),
      };
    case "CapitalHistory":
      return {
        year: data.year,
        called: decimalOrZero(data.called).toNumber(),
        distributed: decimalOrZero(data.distributed).toNumber() * -1,
      };
    case "CapitalActivity":
      return {
        category: data.category,
        value: decimalOrZero(data.value).toNumber(),
      };
    case "Fees":
      return {
        quarter: data.quarter,
        net_fees: decimalOrZero(data.net_fees).toNumber(),
        fee_breaks: decimalOrZero(data.fee_breaks).toNumber() * -1,
        gross_fees: decimalOrZero(data.gross_fees).toNumber(),
        offsets: decimalOrZero(data.offsets).toNumber() * -1,
      };
    case "CreditFacility":
      return {
        row: data.row,
        amount_available: decimalOrZero(data.amount_available).toNumber(),
        amount_outstanding: decimalOrZero(data.amount_outstanding).toNumber(),
      };
  }
};

export const moneyLabelFormatter = (params: { value: string }) =>
  `${divideMoneyStrings(params.value, "1000000")}M`; // just rounding to the nearest million
